import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Agent } from '../agent-builder-services/agent.service';
import { CanonicalTerm, DictionaryService } from '../agent-builder-services/dictionary.service';
import { LanguageServices } from 'src/app/services/LanguageServices';
import { UserAuthenticationServices } from 'src/app/services/user-authentication.services';

/**
 * Manutencao do dicionario canonico do agente.
 *
 * O dicionario liga o jeito que o usuario fala ao termo oficial e ao
 * identificador tecnico. Serve para tres coisas no Java: expandir a busca
 * antes do embedding, montar o glossario do prompt e traduzir o termo para o
 * identificador que o codigo espera.
 */
@Component({
    selector: 'agent-builder-dictionary',
    templateUrl: './agent-builder-dictionary.component.html',
    styleUrls: ['./agent-builder-dictionary.component.scss'],
    standalone: false
})
export class AgentBuilderDictionaryComponent implements OnInit {

    @Input() agent: Agent;

    /** avisa o wizard se ja existe curadoria de verdade (termo com sinonimo) */
    @Output() curadoriaMudou = new EventEmitter<boolean>();

    termos: CanonicalTerm[] = [];
    carregando = false;

    /** texto digitado na busca da grade */
    filtroDeTexto = '';
    /** 'todos', 'global' ou o nome do agente */
    filtroDeEscopo = 'todos';

    // ---- modal de edicao ----
    modalAberto = false;
    salvando = false;
    formulario: FormGroup;
    termoEmEdicao: CanonicalTerm = null;

    /** sinonimos do termo aberto no modal, editados como tags */
    sinonimos: string[] = [];
    novoSinonimo = '';

    // ---- modal de importacao ----
    modalDeImportacaoAberto = false;
    importando = false;
    termosParaImportar: CanonicalTerm[] = [];
    termosJaExistentes: CanonicalTerm[] = [];
    nomeDoArquivo: string = null;
    erroDoArquivo: string = null;

    constructor(
        private dictionaryService: DictionaryService,
        private fb: FormBuilder,
        private message: NzMessageService,
        private modal: NzModalService,
        private ls: LanguageServices,
        private uas: UserAuthenticationServices
    ) {}

    /**
     * Termo global vale para TODOS os agentes, inclusive os de outras equipes.
     * Por isso so o administrador cria: quem cuida de um agente nao deveria
     * poder mexer no vocabulario dos outros sem saber.
     */
    get podeCriarGlobal(): boolean {
        return this.uas?.user?.profile === 'ADMIN';
    }

    ngOnInit(): void {
        this.montarFormulario();
        this.carregarTermos();
    }

    private montarFormulario(): void {

        this.formulario = this.fb.group({
            termoCanonico: ['', Validators.required],
            definicao: [''],
            identificadorTecnico: [''],
            escopo: ['global', Validators.required],
            ativo: [true]
        });
    }

    carregarTermos(): void {

        if (this.agent == null || this.agent._id == null) {
            return;
        }

        this.carregando = true;

        this.dictionaryService.listarTermos(this.agent).subscribe({
            next: (retorno) => {
                this.termos = this.extrairLista(retorno);
                this.carregando = false;
                this.avisarCuradoria();
            },
            error: (erro) => {
                console.error('Erro ao carregar o dicionario:', erro);
                this.termos = [];
                this.carregando = false;
            }
        });
    }

    /**
     * O findnative devolve ora um array direto, ora um objeto com data.
     * Em vez de adivinhar, tratamos os dois casos.
     */
    private extrairLista(retorno: any): CanonicalTerm[] {

        if (retorno == null) {
            return [];
        }

        if (Array.isArray(retorno)) {
            return retorno as CanonicalTerm[];
        }

        if (Array.isArray(retorno.data)) {
            return retorno.data as CanonicalTerm[];
        }

        return [];
    }

    /** termos que sobrevivem aos filtros da tela */
    get termosFiltrados(): CanonicalTerm[] {

        let resultado: CanonicalTerm[] = [];
        let busca = this.filtroDeTexto.trim().toLowerCase();

        for (let termo of this.termos) {

            if (this.filtroDeEscopo !== 'todos' && termo.escopo !== this.filtroDeEscopo) {
                continue;
            }

            if (busca.length === 0) {
                resultado.push(termo);
                continue;
            }

            if (this.termoCasaComABusca(termo, busca)) {
                resultado.push(termo);
            }
        }

        return resultado;
    }

    /** a busca olha o termo, o identificador e tambem os sinonimos */
    private termoCasaComABusca(termo: CanonicalTerm, busca: string): boolean {

        if ((termo.termoCanonico ?? '').toLowerCase().indexOf(busca) >= 0) {
            return true;
        }

        if ((termo.identificadorTecnico ?? '').toLowerCase().indexOf(busca) >= 0) {
            return true;
        }

        if (termo.sinonimos != null) {
            for (let sinonimo of termo.sinonimos) {
                if (sinonimo.toLowerCase().indexOf(busca) >= 0) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * O passo so conta como pronto quando existe termo COM sinonimo: importar
     * o toolbox sozinho nao muda nada no comportamento do agente.
     */
    private avisarCuradoria(): void {

        let temCuradoria = false;

        for (let termo of this.termos) {
            if (termo.sinonimos != null && termo.sinonimos.length > 0) {
                temCuradoria = true;
                break;
            }
        }

        this.curadoriaMudou.emit(temCuradoria);
    }

    /** quantos termos ainda estao sem sinonimo, que e o que faz a busca casar */
    get quantidadeSemSinonimo(): number {

        let total = 0;

        for (let termo of this.termos) {
            if (termo.sinonimos == null || termo.sinonimos.length === 0) {
                total = total + 1;
            }
        }

        return total;
    }

    // ------------------------------------------------------------------
    // Edicao
    // ------------------------------------------------------------------

    novoTermo(): void {

        this.termoEmEdicao = null;
        this.sinonimos = [];
        this.novoSinonimo = '';

        this.formulario.reset({
            termoCanonico: '',
            definicao: '',
            identificadorTecnico: '',
            escopo: this.podeCriarGlobal ? 'global' : (this.agent?.agentName ?? 'global'),
            ativo: true
        });

        this.modalAberto = true;
    }

    editarTermo(termo: CanonicalTerm): void {

        this.termoEmEdicao = termo;
        // copia a lista: se o usuario cancelar, o termo da grade fica intacto
        this.sinonimos = (termo.sinonimos ?? []).slice();
        this.novoSinonimo = '';

        this.formulario.reset({
            termoCanonico: termo.termoCanonico,
            definicao: termo.definicao ?? '',
            identificadorTecnico: termo.identificadorTecnico ?? '',
            escopo: termo.escopo,
            ativo: termo.ativo !== false
        });

        this.modalAberto = true;
    }

    fecharModal(): void {
        this.modalAberto = false;
    }

    adicionarSinonimo(): void {

        let texto = this.novoSinonimo.trim();

        if (texto.length === 0) {
            return;
        }

        if (this.sinonimos.indexOf(texto) >= 0) {
            this.novoSinonimo = '';
            return;
        }

        this.sinonimos.push(texto);
        this.novoSinonimo = '';
    }

    removerSinonimo(indice: number): void {
        this.sinonimos.splice(indice, 1);
    }

    salvar(): void {

        if (this.formulario.invalid) {
            Object.values(this.formulario.controls).forEach(controle => {
                controle.markAsDirty();
                controle.updateValueAndValidity();
            });
            return;
        }

        let valores = this.formulario.value;

        let termo: CanonicalTerm = {
            _id: this.termoEmEdicao != null ? this.termoEmEdicao._id : null,
            termoCanonico: valores.termoCanonico,
            sinonimos: this.sinonimos,
            definicao: valores.definicao,
            identificadorTecnico: valores.identificadorTecnico,
            escopo: valores.escopo,
            // tudo que passa por esta tela vira 'manual': um termo importado
            // que ganhou curadoria nao pode ser tratado como importado de novo
            origem: 'manual',
            ativo: valores.ativo
        };

        this.salvando = true;

        let chamada = this.termoEmEdicao != null
            ? this.dictionaryService.atualizarTermo(this.agent, termo)
            : this.dictionaryService.criarTermo(this.agent, termo);

        // o escopo ANTIGO precisa entrar na conta: um termo que era global e
        // virou especifico continua na copia em cache de todos os outros agentes
        let escopoAnterior = this.termoEmEdicao != null ? this.termoEmEdicao.escopo : null;

        chamada.subscribe({
            next: () => {
                this.salvando = false;
                this.modalAberto = false;
                this.dictionaryService.descartarCache(this.escopoParaDescarte(termo.escopo, escopoAnterior));
                this.message.success(this.ls.instant('agentBuilder.dictionary.saved'));
                this.carregarTermos();
            },
            error: (erro) => {
                console.error('Erro ao salvar o termo:', erro);
                this.salvando = false;
                this.message.error(this.ls.instant('agentBuilder.dictionary.saveError'));
            }
        });
    }

    /**
     * Decide o alcance do descarte de cache.
     *
     * Basta um dos lados ser global para o cache inteiro precisar cair: um termo
     * global vale para todos os agentes, tanto ao passar a existir quanto ao
     * deixar de ser global.
     */
    private escopoParaDescarte(escopoNovo: string, escopoAnterior: string): string {

        if (escopoNovo === 'global') {
            return 'global';
        }

        if (escopoAnterior === 'global') {
            return 'global';
        }

        return escopoNovo;
    }

    confirmarRemocao(termo: CanonicalTerm): void {

        this.modal.confirm({
            nzTitle: this.ls.instant('agentBuilder.dictionary.removeTitle'),
            nzContent: termo.termoCanonico,
            nzOkDanger: true,
            nzOnOk: () => this.removerTermo(termo)
        });
    }

    private removerTermo(termo: CanonicalTerm): void {

        this.dictionaryService.removerTermo(this.agent, termo).subscribe({
            next: () => {
                this.dictionaryService.descartarCache(termo.escopo);
                this.message.success(this.ls.instant('agentBuilder.dictionary.removed'));
                this.carregarTermos();
            },
            error: (erro) => {
                console.error('Erro ao remover o termo:', erro);
                this.message.error(this.ls.instant('agentBuilder.dictionary.removeError'));
            }
        });
    }

    // ------------------------------------------------------------------
    // Importacao por arquivo
    // ------------------------------------------------------------------

    /**
     * Separador de coluna do CSV.
     *
     * Ponto-e-virgula porque e o que o Excel em portugues usa por padrao: um
     * arquivo com virgula abre com tudo numa coluna so. Na leitura os dois sao
     * aceitos.
     */
    private readonly SEPARADOR = ';';

    /** separador dos sinonimos dentro da celula, para nao brigar com o de coluna */
    private readonly SEPARADOR_SINONIMOS = '|';

    /**
     * Baixa um modelo de CSV com o cabecalho e algumas linhas de exemplo.
     *
     * As linhas sao so ilustracao do formato: o vocabulario e de cada agente -
     * um agente financeiro, um de suporte e um de fluxos nao compartilham
     * termo nenhum. Quem preenche e quem conhece o dominio.
     */
    baixarModelo(): void {

        let linhas: string[] = [];

        linhas.push(['termo', 'sinonimos', 'definicao', 'identificador', 'escopo', 'ativo']
                .join(this.SEPARADOR));

        // exemplos escolhidos para mostrar o formato, e nao para serem usados:
        // o primeiro com identificador tecnico, o segundo sem, que e o caso
        // comum de vocabulario de negocio
        linhas.push([
            'Nota fiscal de entrada',
            'nota de entrada|NF-e de compra|nota do fornecedor',
            this.protegerCampo('Documento que registra a compra, com fornecedor, itens e impostos.'),
            'NFE_ENTRADA',
            'global',
            'sim'
        ].join(this.SEPARADOR));

        linhas.push([
            'Pedido em separacao',
            'pedido separando|em picking|sendo separado',
            this.protegerCampo('Pedido cujos itens estao sendo retirados do estoque.'),
            '',
            'global',
            'sim'
        ].join(this.SEPARADOR));

        // BOM: sem ele o Excel abre os acentos errados
        let conteudo = '\ufeff' + linhas.join('\r\n');

        let blob = new Blob([conteudo], { type: 'text/csv;charset=utf-8;' });
        let url = URL.createObjectURL(blob);

        let link = document.createElement('a');
        link.href = url;
        link.download = 'modelo-dicionario.csv';
        link.click();

        URL.revokeObjectURL(url);
    }

    /** poe entre aspas quando o texto tem separador, aspas ou quebra de linha */
    private protegerCampo(texto: string): string {

        if (texto == null) {
            return '';
        }

        if (texto.indexOf(this.SEPARADOR) >= 0 || texto.indexOf('"') >= 0 || texto.indexOf('\n') >= 0) {
            return '"' + texto.replace(/"/g, '""') + '"';
        }

        return texto;
    }

    abrirImportacao(): void {

        this.termosParaImportar = [];
        this.termosJaExistentes = [];
        this.erroDoArquivo = null;
        this.nomeDoArquivo = null;
        this.modalDeImportacaoAberto = true;
    }

    /**
     * Le o arquivo escolhido e monta a previa.
     *
     * O retorno do input e um FileList; o componente nz-upload nao entra aqui
     * de proposito, porque ele quer enviar para um endpoint e a leitura e toda
     * local.
     */
    arquivoEscolhido(evento: any): void {

        let arquivo = evento?.target?.files?.[0];

        if (arquivo == null) {
            return;
        }

        this.nomeDoArquivo = arquivo.name;
        this.erroDoArquivo = null;

        let leitor = new FileReader();

        leitor.onload = () => {
            this.interpretarCsv(leitor.result as string);
        };

        leitor.onerror = () => {
            this.erroDoArquivo = this.ls.instant('agentBuilder.dictionary.readError');
        };

        // UTF-8 cobre o arquivo salvo pelo Excel com BOM e o salvo por editor de texto
        leitor.readAsText(arquivo, 'UTF-8');
    }

    /** transforma o texto do arquivo em termos, separando o que ja existe */
    private interpretarCsv(texto: string): void {

        this.termosParaImportar = [];
        this.termosJaExistentes = [];

        if (texto == null || texto.trim().length === 0) {
            this.erroDoArquivo = this.ls.instant('agentBuilder.dictionary.emptyFile');
            return;
        }

        // o BOM vira um caractere invisivel no inicio e estragaria o primeiro cabecalho
        let limpo = texto.replace(/^\ufeff/, '');
        let linhas = limpo.split(/\r?\n/);

        let separador = this.descobrirSeparador(linhas[0]);
        let colunas = this.mapearColunas(linhas[0], separador);

        if (colunas.termo < 0) {
            this.erroDoArquivo = this.ls.instant('agentBuilder.dictionary.noTermColumn');
            return;
        }

        for (let i = 1; i < linhas.length; i++) {

            let linha = linhas[i];

            if (linha == null || linha.trim().length === 0) {
                continue;
            }

            let campos = this.quebrarLinha(linha, separador);
            let termoCanonico = (campos[colunas.termo] ?? '').trim();

            if (termoCanonico.length === 0) {
                continue;
            }

            let termo: CanonicalTerm = {
                termoCanonico: termoCanonico,
                sinonimos: this.lerSinonimos(campos[colunas.sinonimos]),
                definicao: (campos[colunas.definicao] ?? '').trim(),
                identificadorTecnico: (campos[colunas.identificador] ?? '').trim(),
                escopo: this.lerEscopo(campos[colunas.escopo]),
                origem: 'manual',
                ativo: this.lerSimNao(campos[colunas.ativo])
            };

            if (this.jaExisteNoDicionario(termo)) {
                this.termosJaExistentes.push(termo);
            } else {
                this.termosParaImportar.push(termo);
            }
        }

        if (this.termosParaImportar.length === 0 && this.termosJaExistentes.length === 0) {
            this.erroDoArquivo = this.ls.instant('agentBuilder.dictionary.noRows');
        }
    }

    /** aceita ; ou , como separador, escolhendo o que aparece mais no cabecalho */
    private descobrirSeparador(cabecalho: string): string {

        if (cabecalho == null) {
            return this.SEPARADOR;
        }

        let comPontoVirgula = cabecalho.split(';').length;
        let comVirgula = cabecalho.split(',').length;

        return comVirgula > comPontoVirgula ? ',' : ';';
    }

    /**
     * Descobre a posicao de cada coluna pelo cabecalho.
     *
     * Assim a ordem das colunas no arquivo nao importa, e uma coluna a mais
     * (alguem que anotou algo ao lado) nao atrapalha.
     */
    private mapearColunas(cabecalho: string, separador: string): any {

        let nomes = this.quebrarLinha(cabecalho ?? '', separador);
        let posicoes = { termo: -1, sinonimos: -1, definicao: -1, identificador: -1, escopo: -1, ativo: -1 };

        for (let i = 0; i < nomes.length; i++) {

            let nome = this.normalizar(nomes[i]);

            if (nome.indexOf('termo') >= 0)          { posicoes.termo = i; }
            if (nome.indexOf('sinonimo') >= 0)       { posicoes.sinonimos = i; }
            if (nome.indexOf('definicao') >= 0)      { posicoes.definicao = i; }
            if (nome.indexOf('identificador') >= 0)  { posicoes.identificador = i; }
            if (nome.indexOf('escopo') >= 0)         { posicoes.escopo = i; }
            if (nome.indexOf('ativo') >= 0)          { posicoes.ativo = i; }
        }

        return posicoes;
    }

    /** minusculo e sem acento, para o cabecalho casar escrito de qualquer jeito */
    private normalizar(texto: string): string {

        if (texto == null) {
            return '';
        }

        return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    }

    /**
     * Quebra a linha respeitando as aspas.
     *
     * Um split simples cortaria no meio de uma definicao que contenha o
     * separador - e definicao com virgula e o caso comum, nao a excecao.
     */
    private quebrarLinha(linha: string, separador: string): string[] {

        let campos: string[] = [];
        let atual = '';
        let dentroDeAspas = false;

        for (let i = 0; i < linha.length; i++) {

            let caractere = linha[i];

            if (caractere === '"') {

                // duas aspas seguidas dentro do campo representam uma aspa literal
                if (dentroDeAspas && linha[i + 1] === '"') {
                    atual = atual + '"';
                    i = i + 1;
                    continue;
                }

                dentroDeAspas = !dentroDeAspas;
                continue;
            }

            if (caractere === separador && !dentroDeAspas) {
                campos.push(atual);
                atual = '';
                continue;
            }

            atual = atual + caractere;
        }

        campos.push(atual);

        return campos;
    }

    /** aceita | ou , separando os sinonimos dentro da celula */
    private lerSinonimos(celula: string): string[] {

        if (celula == null || celula.trim().length === 0) {
            return [];
        }

        let partes = celula.split(this.SEPARADOR_SINONIMOS);

        if (partes.length === 1) {
            partes = celula.split(',');
        }

        let resultado: string[] = [];

        for (let parte of partes) {

            let limpo = parte.trim();

            if (limpo.length > 0 && resultado.indexOf(limpo) < 0) {
                resultado.push(limpo);
            }
        }

        return resultado;
    }

    /**
     * Vazio ou "global" viram global; qualquer outra coisa e o nome do agente.
     *
     * Sem permissao de administrador, a linha marcada como global entra no
     * escopo do agente - o arquivo nao pode ser um caminho para contornar a
     * regra da tela.
     */
    private lerEscopo(celula: string): string {

        let valor = (celula ?? '').trim();
        let querGlobal = valor.length === 0 || this.normalizar(valor) === 'global';

        if (querGlobal && this.podeCriarGlobal) {
            return 'global';
        }

        return this.agent?.agentName ?? 'global';
    }

    /** sim, s, true, 1 e vazio contam como ativo */
    private lerSimNao(celula: string): boolean {

        let valor = this.normalizar(celula ?? '');

        if (valor.length === 0) {
            return true;
        }

        return valor === 'sim' || valor === 's' || valor === 'true' || valor === '1';
    }

    /**
     * Compara pelo identificador tecnico quando existe, porque e ele que o
     * codigo usa. Sem identificador, sobra comparar pelo proprio termo.
     */
    private jaExisteNoDicionario(candidato: CanonicalTerm): boolean {

        for (let existente of this.termos) {

            if (candidato.identificadorTecnico != null && candidato.identificadorTecnico.length > 0) {

                if (existente.identificadorTecnico === candidato.identificadorTecnico) {
                    return true;
                }

                continue;
            }

            if ((existente.termoCanonico ?? '').toLowerCase() === candidato.termoCanonico.toLowerCase()) {
                return true;
            }
        }

        return false;
    }

    fecharImportacao(): void {
        this.modalDeImportacaoAberto = false;
    }

    confirmarImportacao(): void {

        if (this.termosParaImportar.length === 0) {
            this.modalDeImportacaoAberto = false;
            return;
        }

        this.importando = true;

        let restantes = this.termosParaImportar.length;
        let comErro = 0;

        for (let termo of this.termosParaImportar) {

            this.dictionaryService.criarTermo(this.agent, termo).subscribe({
                next: () => {
                    restantes = restantes - 1;
                    this.encerrarImportacaoSeTerminou(restantes, comErro);
                },
                error: (erro) => {
                    console.error('Erro ao importar o termo ' + termo.termoCanonico, erro);
                    comErro = comErro + 1;
                    restantes = restantes - 1;
                    this.encerrarImportacaoSeTerminou(restantes, comErro);
                }
            });
        }
    }

    /** so fecha o modal quando a ultima gravacao voltou, com erro ou sem */
    private encerrarImportacaoSeTerminou(restantes: number, comErro: number): void {

        if (restantes > 0) {
            return;
        }

        this.importando = false;
        this.modalDeImportacaoAberto = false;

        // o arquivo pode trazer termos de qualquer escopo, entao o cache de
        // todos os agentes envelheceu
        this.dictionaryService.descartarCache('global');

        if (comErro > 0) {
            this.message.warning(this.ls.instant('agentBuilder.dictionary.importPartial'));
        } else {
            this.message.success(this.ls.instant('agentBuilder.dictionary.imported'));
        }

        this.carregarTermos();
    }

}
